import { useContext, useEffect, useState } from "react";
import { Checkbox, Form, Input, Select, notification } from "antd";
import { defineMessages, FormattedMessage, useIntl, type MessageDescriptor } from "react-intl";
import { UserHttpService } from "@/shared/services/user/user-http.service";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import {
  AssessmentLogEventsEnum,
  AssessmentLogsHttpService,
} from "@/shared/services/assessments/assessment-logs-http.service";
import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { useLocale } from "@/i18n";
import { UserContext } from "@/shared/contexts/user.context";
import { IframeModal } from "../modals/IframeModal/IframeModal";
import { useDemographicsOptions } from "./useDemographicsOptions.hook";
import { DemographicChoice, DemographicQuestion, getLocalizedLabel } from "./demographics.types";
import { Loading, LoadingSize } from "@/shared/components/Loading/Loading";
import { getLocalizedUrl, LocaleUrlType } from "@/shared/utils/locale-urls.utils";
import style from "./style.module.css";
import classNames from "classnames";

enum IntakeModalType {
  Terms = "terms",
  Attestation = "attestation",
}

const messages = defineMessages({
  savingPleaseWait: {
    id: "candidate.intake.savingPleaseWait",
    defaultMessage: "Please wait",
  },
  formOptionsLoading: {
    id: "candidate.intake.formOptionsLoading",
    defaultMessage: "Form options are still loading. Try again in a moment.",
  },
  genericError: {
    id: "candidate.errors.genericRetry",
    defaultMessage: "Oops, something went wrong. Please try again.",
  },
  pleaseSelectAnswer: {
    id: "candidate.intake.pleaseSelectAnswer",
    defaultMessage: "Please select an answer.",
  },
  pleaseSelectOption: {
    id: "candidate.intake.pleaseSelectOption",
    defaultMessage: "Please select at least one option.",
  },
  declinedExclusive: {
    id: "candidate.intake.declinedExclusive",
    defaultMessage: "If you prefer not to answer, you cannot select other options.",
  },
  selectOnePlaceholder: {
    id: "candidate.intake.selectOnePlaceholder",
    defaultMessage: "Select One",
  },
  employeeIdRequired: {
    id: "candidate.intake.employeeIdRequired",
    defaultMessage: "Employee ID is required",
  },
  employeeIdMaxLength: {
    id: "candidate.intake.employeeIdMaxLength",
    defaultMessage: "Employee ID must be 100 characters or less",
  },
  typePlaceholder: {
    id: "candidate.intake.typePlaceholder",
    defaultMessage: "Type...",
  },
  termsRequired: {
    id: "candidate.intake.termsRequired",
    defaultMessage: "You must agree to the Terms of Service before continuing.",
  },
  attestationRequired: {
    id: "candidate.intake.attestationRequired",
    defaultMessage: "You must agree to the Attestation of Identity before continuing.",
  },
  termsModalTitle: {
    id: "candidate.intake.termsModalTitle",
    defaultMessage: "Terms of Service",
  },
  attestationModalTitle: {
    id: "candidate.intake.attestationModalTitle",
    defaultMessage: "Attestation of Identity",
  },
});

const INTAKE_MODAL: Record<IntakeModalType, { title: MessageDescriptor; urlType: LocaleUrlType }> = {
  [IntakeModalType.Terms]: { title: messages.termsModalTitle, urlType: LocaleUrlType.TermsOfService },
  [IntakeModalType.Attestation]: { title: messages.attestationModalTitle, urlType: LocaleUrlType.Attestation },
};

const DECLINED_CHOICE_KEY = "declined";

const STICKY_FOOTER_SCROLL_OFFSET = -75;

function isViewportScroll(el: Element): boolean {
  return el === document.documentElement || el === document.body;
}

/** Custom scroll behavior that offsets the content scroll container by the sticky footer height so the first error isn't hidden. */
const scrollToFirstErrorOptions = {
  block: "nearest" as const,
  behavior: (actions: { el: Element; top: number; left: number }[]) => {
    let lastContentIndex = -1;
    for (let i = 0; i < actions.length; i++) {
      if (!isViewportScroll(actions[i].el)) lastContentIndex = i;
    }
    actions.forEach((action, index) => {
      const shouldOffset = index === lastContentIndex;
      const top = shouldOffset ? Math.max(0, action.top - STICKY_FOOTER_SCROLL_OFFSET) : action.top;
      (action.el as HTMLElement).scrollTop = top;
      (action.el as HTMLElement).scrollLeft = action.left;
    });
  },
};

// Form item injects props to its child, so we need to pass them through
function MultiSelectControl({ choices, ...props }: { choices: DemographicChoice[] }) {
  return (
    <>
      <p className={style.selectAllText}>
        <FormattedMessage id="candidate.intake.selectAllThatApply" defaultMessage="Select all that apply" />
      </p>
      <Checkbox.Group {...props}>
        {choices.map((choice) => (
          <Checkbox key={choice.key} value={choice.key}>
            {getLocalizedLabel(choice.label)}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </>
  );
}

export const ASSESSMENT_INTAKE_FORM_ID = "assessmentIntakeForm";

interface IAssessmentIntakeFormProps {
  hasAcceptedTerms: boolean;
  inviteId?: number;
  testId?: number;
  onSubmit: () => Promise<void>;
  demographicsEnabled?: boolean;
  employeeIdEnabled?: boolean;
  demographicsSubmittedAt?: string | null;
  employeeId?: string | null;
  requiresIdentityAttestation: boolean;
}

interface IAssessmentIntakeFormValues {
  demographics?: Record<string, string | string[] | undefined>;
  employeeId?: string;
  termsAccepted: boolean;
  attestationAccepted: boolean;
}

function getSelectedValues(type: string, raw: string | string[] | boolean | undefined): string[] {
  if (type === "multi_select") {
    return Array.isArray(raw) ? raw : [];
  }
  return raw != null && raw !== "" ? [String(raw)] : [];
}

function isAnswered(value: string | string[] | undefined): boolean {
  return Array.isArray(value) ? value.length > 0 : value != null && value !== "";
}

function getDemographicsDefaults(questions: DemographicQuestion[]): Record<string, string | string[] | undefined> {
  return questions.reduce<Record<string, string | string[] | undefined>>((acc, q) => {
    acc[q.key] = q.type === "multi_select" ? [] : undefined;
    return acc;
  }, {});
}

export default function AssessmentIntakeForm({
  hasAcceptedTerms,
  inviteId,
  testId,
  onSubmit,
  demographicsEnabled = false,
  employeeIdEnabled = false,
  demographicsSubmittedAt,
  employeeId: inviteEmployeeId,
  requiresIdentityAttestation,
}: IAssessmentIntakeFormProps) {
  const [form] = Form.useForm<IAssessmentIntakeFormValues>();
  const intl = useIntl();
  // The language choice is owned by the intro view's "Change Language" modal, which persists it and
  // switches the app locale. We read that locale here so the terms/attestation URL follows the choice.
  const { locale: appLocale } = useLocale();
  const { setUser } = useContext(UserContext);
  const [intakeModalType, setIntakeModalType] = useState<IntakeModalType | null>(null);
  const demographicsAlreadyCollected = Boolean(demographicsSubmittedAt);

  // Load options, regardless of whether they are enabled or not to ensure that the latest settings are used
  const {
    questions,
    isLoading: isLoadingOptions,
    error: demographicsError,
  } = useDemographicsOptions({ inviteId, demographicsAlreadyCollected, locale: appLocale });
  const employeeIdAlreadyCollected = inviteEmployeeId != null && String(inviteEmployeeId).trim() !== "";

  const shouldCollectDemographics = demographicsEnabled && !demographicsAlreadyCollected && !demographicsError;
  const formId = ASSESSMENT_INTAKE_FORM_ID;

  useEffect(() => {
    if (questions.length === 0) return;

    const demographicsDefaults = getDemographicsDefaults(questions);
    const current = form.getFieldValue("demographics") ?? {};
    const seeded = Object.fromEntries(
      Object.entries(demographicsDefaults).map(([key, empty]) => [
        key,
        isAnswered(current[key]) ? current[key] : empty,
      ]),
    );

    form.setFieldsValue({ demographics: seeded });
  }, [questions, form]);

  const handleFinish = async (values: IAssessmentIntakeFormValues) => {
    if (isLoadingOptions) {
      notification.info({
        message: intl.formatMessage(messages.savingPleaseWait),
        description: intl.formatMessage(messages.formOptionsLoading),
      });
      return;
    }

    const needsDemographicsSave = shouldCollectDemographics && questions.length > 0;
    const needsEmployeeIdSave = employeeIdEnabled && !employeeIdAlreadyCollected;

    if (inviteId && (needsDemographicsSave || needsEmployeeIdSave)) {
      try {
        const responses = needsDemographicsSave
          ? questions.map((q) => ({
              questionKey: q.key,
              selectedValues: getSelectedValues(q.type, values.demographics?.[q.key]),
            }))
          : [];
        await InvitesHttpService.saveDemographics(inviteId, {
          employeeId: values.employeeId,
          responses,
        });
      } catch (error) {
        return notification.error({
          message: getFriendlyErrorMessage(error) ?? intl.formatMessage(messages.genericError),
        });
      }
    }

    if (!hasAcceptedTerms) {
      try {
        await UserHttpService.acceptTermsOfService();

        if (setUser) {
          setUser((prevUser) => {
            if (!prevUser) return prevUser;
            return { ...prevUser, termsOfServiceAcceptedAt: new Date().toISOString() };
          });
        }
      } catch (error) {
        return notification.error({
          message: getFriendlyErrorMessage(error) ?? intl.formatMessage(messages.genericError),
        });
      }
    }

    if (requiresIdentityAttestation && inviteId && testId) {
      try {
        await AssessmentLogsHttpService.saveInviteLogs(inviteId, testId, AssessmentLogEventsEnum.IDENTITY_ATTESTED);
      } catch (error) {
        return notification.error({
          message: getFriendlyErrorMessage(error) ?? intl.formatMessage(messages.genericError),
        });
      }
    }

    await onSubmit();
  };

  // Ignore demographic loading error to favor getting candidates into the test
  const shouldCollectEmployeeId = employeeIdEnabled && !employeeIdAlreadyCollected;
  const shouldCollectTermsAcceptance = !hasAcceptedTerms;
  const shouldCollectAgreements = shouldCollectTermsAcceptance || requiresIdentityAttestation;

  return (
    <>
      <Form<IAssessmentIntakeFormValues>
        form={form}
        id={formId}
        name={formId}
        onFinish={handleFinish}
        initialValues={{
          termsAccepted: hasAcceptedTerms,
          attestationAccepted: false,
          ...(shouldCollectEmployeeId && { employeeId: inviteEmployeeId ?? "" }),
          ...(shouldCollectDemographics &&
            questions.length > 0 && {
              demographics: getDemographicsDefaults(questions),
            }),
        }}
        className={`${style.form} assessment-intake-form`}
        layout="vertical"
        requiredMark={false}
        scrollToFirstError={scrollToFirstErrorOptions}
      >
        <div>
          {shouldCollectDemographics && (
            <div>
              <h3 className={style.demographicsTitle}>
                <FormattedMessage id="candidate.intake.demographicsTitle" defaultMessage="Demographics" />
              </h3>
              <p className={style.demographicsDescription}>
                <FormattedMessage
                  id="candidate.intake.demographicsDescription"
                  defaultMessage="Responses to the following questions are voluntary and used for compliance with federal regulations."
                />
              </p>

              {isLoadingOptions ? (
                <div className={style.demographicsLoading}>
                  <Loading size={LoadingSize.small} />
                </div>
              ) : (
                questions.map((question) => (
                  <Form.Item
                    key={question.questionId}
                    name={["demographics", question.key]}
                    label={<span>{getLocalizedLabel(question.text)}</span>}
                    className={classNames(style.formField, style[`demographics-question__${question.type}`], {
                      [style.baseInput]: question.type === "single_select",
                    })}
                    rules={
                      question.type === "single_select"
                        ? [{ required: true, message: intl.formatMessage(messages.pleaseSelectAnswer) }]
                        : [
                            {
                              type: "array",
                              min: 1,
                              message: intl.formatMessage(messages.pleaseSelectOption),
                            },
                            {
                              validator: (_, value: string[]) => {
                                const arr = Array.isArray(value) ? value : [];
                                if (arr.includes(DECLINED_CHOICE_KEY) && arr.length > 1) {
                                  return Promise.reject(new Error(intl.formatMessage(messages.declinedExclusive)));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]
                    }
                  >
                    {question.type === "single_select" ? (
                      <Select
                        placeholder={intl.formatMessage(messages.selectOnePlaceholder)}
                        allowClear
                        loading={isLoadingOptions}
                        options={question.choices.map((c) => ({
                          value: c.key,
                          label: getLocalizedLabel(c.label),
                        }))}
                      />
                    ) : (
                      <MultiSelectControl choices={question.choices} />
                    )}
                  </Form.Item>
                ))
              )}
            </div>
          )}

          {shouldCollectEmployeeId && (
            <div>
              <Form.Item
                name="employeeId"
                label={
                  <span>
                    <FormattedMessage
                      id="candidate.intake.employeeIdLabel"
                      defaultMessage="Please enter your Employee ID"
                    />
                  </span>
                }
                className={classNames(style.formField, style.baseInput)}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage(messages.employeeIdRequired),
                  },
                  {
                    max: 100,
                    message: intl.formatMessage(messages.employeeIdMaxLength),
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage(messages.typePlaceholder)}
                  className={style.employeeIdInput}
                  maxLength={100}
                />
              </Form.Item>
            </div>
          )}
        </div>

        {shouldCollectAgreements && (
          <div className={style.agreementSection}>
            {shouldCollectTermsAcceptance && (
              <Form.Item
                name="termsAccepted"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(new Error(intl.formatMessage(messages.termsRequired))),
                  },
                ]}
              >
                <Checkbox>
                  <FormattedMessage
                    id="candidate.intake.acceptTerms"
                    defaultMessage="Accept <link>Terms of Service</link>"
                    values={{
                      link: (chunks) => (
                        <button
                          type="button"
                          onClick={() => setIntakeModalType(IntakeModalType.Terms)}
                          className={style.link}
                        >
                          {chunks}
                        </button>
                      ),
                    }}
                  />
                </Checkbox>
              </Form.Item>
            )}

            {requiresIdentityAttestation && (
              <Form.Item
                name="attestationAccepted"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error(intl.formatMessage(messages.attestationRequired))),
                  },
                ]}
              >
                <Checkbox>
                  <FormattedMessage
                    id="candidate.intake.acceptAttestation"
                    defaultMessage="I confirm that I have read, understand, and agree to the <link>Attestation of Identity</link>"
                    values={{
                      link: (chunks) => (
                        <button
                          type="button"
                          onClick={() => setIntakeModalType(IntakeModalType.Attestation)}
                          className={style.link}
                        >
                          {chunks}
                        </button>
                      ),
                    }}
                  />
                </Checkbox>
              </Form.Item>
            )}
          </div>
        )}
      </Form>

      {intakeModalType && (
        <IframeModal
          isOpen={true}
          onClose={() => setIntakeModalType(null)}
          url={getLocalizedUrl(INTAKE_MODAL[intakeModalType].urlType, appLocale)}
          title={intl.formatMessage(INTAKE_MODAL[intakeModalType].title)}
        />
      )}
    </>
  );
}
